import { newIdempotencyKey } from '@micarahr/api-client';
import { apiTransport } from './api-client';
import type { Page } from './collections';
import type { AdminLeaveRequest, AdminOvertimeRequest } from './seed';
import { narrow, REQUEST_STATUSES } from './narrow';
import { decideRequest, type RequestDecision } from './request-decisions.api';
import { cursorPage } from './cursor-page';

/**
 * Leave and overtime administration.
 *
 * Two things the prototype could take for granted and this cannot.
 *
 * **The step matters.** The server runs a real approval chain, so "approve"
 * is not one action — it is `approve-manager` or `approve-hr`, and calling the
 * wrong one gets a clear refusal naming the step that is waiting. The adapter
 * exposes both rather than guessing, because guessing produces a request that
 * silently sits at a step nobody actioned.
 *
 * **Overtime has two hour figures.** What the employee asked for and what was
 * approved are different numbers and both survive; the approval carries the
 * approved figure only when a reviewer trimmed it. Sending the estimate back as
 * though it were a decision would overwrite the distinction the module exists
 * to keep.
 */

interface ApiEmployeeRef {
  id: string;
  employeeNumber: string;
  fullName: string;
  department: { id: string; name: string } | null;
}

interface ApiLeaveRequest {
  id: string;
  referenceCode: string;
  employeeId: string;
  employee: (ApiEmployeeRef & { manager?: { fullName: string } | null }) | null;
  leaveType: { id: string; code: string; name: string } | null;
  startDate: string;
  endDate: string;
  totalDays: string | number;
  reason: string | null;
  status: string;
  decisionComment: string | null;
  handoverNotes: string | null;
  teamConflictCount: number | null;
  balanceBefore: string | number | null;
  balanceAfter: string | number | null;
  decidedAt: string | null;
  decidedByUser?: { displayName: string } | null;
  createdAt: string;
}

interface ApiOvertimeRequest {
  id: string;
  referenceCode: string;
  employeeId: string;
  employee: ApiEmployeeRef | null;
  date: string;
  overtimeType: string;
  estimatedHours: string | number;
  approvedHours: string | number | null;
  reason: string | null;
  status: string;
  payrollReady: boolean;
  /* The planned window and what it was for — filed from the phone since
     these fields existed; null on older requests. */
  startTime?: string | null;
  endTime?: string | null;
  workPerformed?: string | null;
  projectReference?: string | null;
  decisionComment: string | null;
  decidedAt: string | null;
  decidedByUser?: { displayName: string } | null;
  employeeManagerName?: string | null;
  steps?: {
    label: string;
    status: string;
    decidedAt: string | null;
    decidedByName: string | null;
    comment: string | null;
    approvedHours: number | null;
  }[];
  createdAt: string;
}

/**
 * Decimal fields arrive as strings.
 *
 * Prisma serialises them without trailing zeros, so the same value reaches the
 * client as `-3` in one field and `12.00` in another. Parsing once here means
 * no screen compares them as strings and finds `"3"` unequal to `"3.00"`.
 */
function toNumber(value: string | number | null): number {
  if (value === null) return 0;
  return typeof value === 'number' ? value : Number(value);
}

/**
 * The balances either side of the request.
 *
 * Null when the server did not send them, which is not the same as a balance
 * of zero — and on an approval screen the difference decides whether somebody
 * clicks approve. The prototype's type made these plain numbers and the
 * adapter's cast let `0` through for "unknown"; both are now nullable.
 */
function toBalance(value: string | number | null): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toLeaveRequest(row: ApiLeaveRequest): AdminLeaveRequest {
  return {
    id: row.id,
    referenceCode: row.referenceCode,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    departmentId: row.employee?.department?.id ?? '',
    departmentName: row.employee?.department?.name ?? '',
    managerName: row.employee?.manager?.fullName ?? null,
    leaveTypeId: row.leaveType?.id ?? '',
    leaveTypeName: row.leaveType?.name ?? '',
    startDate: row.startDate.slice(0, 10),
    endDate: row.endDate.slice(0, 10),
    totalDays: toNumber(row.totalDays),
    reason: row.reason ?? '',
    status: narrow(REQUEST_STATUSES, row.status, 'leave request status'),
    submittedAt: row.createdAt,
    decidedAt: row.decidedAt,
    decidedByName: row.decidedByUser?.displayName ?? null,
    hrComment: row.decisionComment,
    // The API stores attachments as media records reached from the request
    // detail, so a list row cannot name one. Null says "not known here"; an
    // empty string would have claimed there is none.
    attachmentName: null,
    handoverNotes: row.handoverNotes,
    // Counted by the server when the request was filed, against the roster as
    // it stood then. Not recomputed here: a second count taken today would
    // disagree with the one the employee saw, and neither would be wrong.
    teamConflictCount: row.teamConflictCount ?? 0,
    balanceBefore: toBalance(row.balanceBefore),
    balanceAfter: toBalance(row.balanceAfter),
  };
}

function toOvertimeRequest(row: ApiOvertimeRequest): AdminOvertimeRequest {
  return {
    id: row.id,
    referenceCode: row.referenceCode,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    departmentId: row.employee?.department?.id ?? '',
    departmentName: row.employee?.department?.name ?? '',
    managerName: row.employeeManagerName ?? null,
    date: row.date.slice(0, 10),
    startTime: row.startTime ?? null,
    endTime: row.endTime ?? null,
    workPerformed: row.workPerformed ?? null,
    projectReference: row.projectReference ?? null,
    costCenterId: null,
    overtimeType: row.overtimeType,
    // Both figures. A screen showing only the approved hours hides that
    // somebody asked for more and was cut back.
    estimatedHours: toNumber(row.estimatedHours),
    approvedHours: row.approvedHours === null ? null : toNumber(row.approvedHours),
    reason: row.reason ?? '',
    status: narrow(REQUEST_STATUSES, row.status, 'overtime request status'),
    payrollReady: row.payrollReady,
    submittedAt: row.createdAt,
    decidedAt: row.decidedAt,
    decidedByName: row.decidedByUser?.displayName ?? null,
    // The reviewer's comment. The panel said the comment was "above" and
    // never showed it.
    hrComment: row.decisionComment,
    steps: row.steps,
  };
}

export interface QueueFilter {
  status?: string;
  search?: string;
  departmentId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

async function listQueue<TApi, TOut>(
  path: string,
  filter: QueueFilter,
  map: (row: TApi) => TOut,
): Promise<Page<TOut>> {
  return cursorPage<TApi, TOut>(
    path,
    {
      status: filter.status === 'all' ? undefined : filter.status,
      departmentId: filter.departmentId,
      from: filter.from,
      to: filter.to,
      // The queues' search boxes were sent nowhere.
      search: filter.search?.trim() || undefined,
    },
    filter,
    map,
  );
}

export const apiLeaveAdminService = {
  list(filter: QueueFilter = {}): Promise<Page<AdminLeaveRequest>> {
    return listQueue<ApiLeaveRequest, AdminLeaveRequest>(
      '/api/v1/leave-requests',
      filter,
      toLeaveRequest,
    );
  },

  async get(id: string): Promise<AdminLeaveRequest | null> {
    try {
      return toLeaveRequest(
        await apiTransport().get<ApiLeaveRequest>(`/api/v1/leave-requests/${id}`),
      );
    } catch (error) {
      if ((error as { status?: number }).status === 404) return null;
      throw error;
    }
  },

  /**
   * Decide a step.
   *
   * `step` is explicit rather than inferred from the viewer's role: a person
   * may hold both the manager and HR permissions, and inferring would pick one
   * arbitrarily. The server refuses a decision on a step that is not current
   * and says which one is waiting.
   */
  /** What the queues and the Approvals centre call; see `decideRequest`. */
  decide(id: string, decision: RequestDecision, comment: string): Promise<unknown> {
    return decideRequest('leave-requests', id, decision, comment);
  },

  approve(id: string, step: 'manager' | 'hr', comment: string): Promise<unknown> {
    return apiTransport().post(
      `/api/v1/leave-requests/${id}/approve-${step}`,
      { comment },
      // Keyed: a reviewer double-clicking Approve must decide once, not twice.
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  reject(id: string, comment: string): Promise<unknown> {
    return apiTransport().post(
      `/api/v1/leave-requests/${id}/reject`,
      { comment },
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  cancelApproved(id: string, reason: string): Promise<unknown> {
    return apiTransport().post(`/api/v1/leave-requests/${id}/cancel-approved`, { reason });
  },
};

export const apiOvertimeAdminService = {
  list(filter: QueueFilter = {}): Promise<Page<AdminOvertimeRequest>> {
    return listQueue<ApiOvertimeRequest, AdminOvertimeRequest>(
      '/api/v1/overtime-requests',
      filter,
      toOvertimeRequest,
    );
  },

  async get(id: string): Promise<AdminOvertimeRequest | null> {
    try {
      return toOvertimeRequest(
        await apiTransport().get<ApiOvertimeRequest>(`/api/v1/overtime-requests/${id}`),
      );
    } catch (error) {
      if ((error as { status?: number }).status === 404) return null;
      throw error;
    }
  },

  /**
   * What the queues and the Approvals centre call; see `decideRequest`.
   * Approved hours travel only when the reviewer set them.
   */
  decide(
    id: string,
    decision: RequestDecision,
    comment: string,
    _actor?: unknown,
    approvedHours?: number,
  ): Promise<unknown> {
    return decideRequest(
      'overtime-requests',
      id,
      decision,
      comment,
      approvedHours === undefined ? {} : { approvedHours },
    );
  },

  /**
   * Approve, optionally for fewer hours than were asked for.
   *
   * `approvedHours` is omitted when the reviewer did not change the figure —
   * sending the estimate back as though it were a decision would look like a
   * deliberate confirmation of hours nobody examined, and the server treats an
   * explicit figure as exactly that.
   */
  approve(
    id: string,
    step: 'manager' | 'hr',
    comment: string,
    approvedHours?: number,
  ): Promise<unknown> {
    return apiTransport().post(
      `/api/v1/overtime-requests/${id}/approve-${step}`,
      { comment, ...(approvedHours === undefined ? {} : { approvedHours }) },
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  reject(id: string, comment: string): Promise<unknown> {
    return apiTransport().post(
      `/api/v1/overtime-requests/${id}/reject`,
      { comment },
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  /** Hand approved hours to payroll. Separate from approving them, deliberately. */
  markPayrollReady(id: string): Promise<unknown> {
    return apiTransport().post(
      `/api/v1/overtime-requests/${id}/mark-payroll-ready`,
      {},
      { idempotencyKey: newIdempotencyKey() },
    );
  },
};
